import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Bell, Search, User, Settings, LogOut, CheckCircle2, Package, FileCheck, AlertCircle, Building2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn, getRoleLabel } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { notificationsApi, wasWorkspaceRecentlySwitched } from '../../lib/api';
import { WorkspaceModuleChips } from './WorkspaceModuleChips';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { useOverviewGlobal } from '../../hooks/useOverviewGlobal';
import { WorkspaceSwitcher } from '../WorkspaceSwitcher';
import { getByRoute, getBreadcrumbTrail } from '../../lib/routeRegistry';
import { useBreadcrumbLabel } from '../../context/BreadcrumbContext';

const routeTitles = {
  '/home': 'Home',
  '/insights': 'Insights',
  '/dashboard': 'Dashboard',
  '/cases': 'Cases',
  '/approvals': 'Approvals Queue',
  '/intake': 'Intake & Tracking',
  '/reports': 'Reports',
  '/data-quality': 'Data Quality',
  '/audit-logs': 'Audit Log',
  '/users': 'Users',
  '/settings': 'Settings',
  '/workflows': 'Workflows',
  '/case-items': 'Case Items',
  '/review-queue': 'Review Queue',
  '/eu-returns': 'EU Returns',
  '/eu-h7-compilation': 'EU H7 Compilation',
  '/filing-hub': 'Filing Hub',
  '/eu-h7-reason-mapper': 'EU H7 Reason Mapper',
  '/import-review': 'Import Review',
  '/rgr-returns': 'RGR Relief',
};

const notificationIcons = {
  'intake_complete': Package,
  'case_status_change': FileCheck,
  'approval_required': AlertCircle,
  'default': Bell
};

export function Header({ className }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, activeWorkspace } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  // Shared exposure-window source of truth — same hook used by
  // EUReturnsPage + ReportsPage. Pill reflects the persisted value
  // immediately because the hook re-syncs on workspace change.
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Iter236 Phase 2 — deep-link pages call `useSetBreadcrumbLabel`
  // to set a dynamic label like `Case: ABC-123`. When present, we
  // override the final crumb's title with that string so ops can see
  // *which* entity they're on without leaving the header.
  const dynamicLabel = useBreadcrumbLabel();
  const overviewGlobal = useOverviewGlobal();

  // iter234 Phase 5c — prefer the route-registry parent-chain trail
  // when the current path is registered (so EU H7 sub-pages surface
  // "EU Returns → EU H7 Compilation" instead of a flat single chip).
  // Fall back to naive path-segment splitting for routes that don't
  // have a registry entry yet — existing pages keep their breadcrumb.
  const registryTrail = getBreadcrumbTrail(location.pathname);
  const rawBreadcrumbs = registryTrail.length > 0
    ? registryTrail
        .filter((e) => e.route !== '/home')
        .map((e) => ({ path: e.route, title: routeTitles[e.route] || e.label }))
    : pathSegments.map((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/');
        const entry = getByRoute(path);
        const title = routeTitles[path]
          || entry?.label
          || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        return { path, title };
      });

  // Apply the dynamic label to the final crumb if a page has set one.
  const breadcrumbs = dynamicLabel && rawBreadcrumbs.length > 0
    ? rawBreadcrumbs.map((c, i) => (
      i === rawBreadcrumbs.length - 1 ? { ...c, title: dynamicLabel } : c
    ))
    : rawBreadcrumbs;



  // Fetch notifications using new API
  useEffect(() => {
    if (user && activeWorkspace?.id) {
      loadNotifications();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, activeWorkspace]);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationsApi.list(),
        notificationsApi.getUnreadCount()
      ]);
      // API returns {notifications: [], total: 0, unread_count: 0, ...}
      setNotifications(notifRes.data.notifications || notifRes.data || []);
      setUnreadCount(countRes.data.count || countRes.data.unread || 0);
    } catch (err) {
      console.error('Failed to load notifications');
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      // Silently fail
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // iter204 — prefer explicit notification.link (backend-set) over
    // legacy case_id inference so deep-links to non-case entities
    // (packs, matches, imports) also work.
    const target = notification.link
      || (notification.case_id ? `/cases/${notification.case_id}` : null);
    if (target) {
      navigate(target);
      setNotifOpen(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cases?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className={cn("app-topbar flex items-center justify-between", className)}>
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-sm">
        {(() => {
          // Iter263 Phase R2 hotfix — partner users get a
          // partner-flavoured trail: "Partner > [Workspace] > [Page]"
          // (workspace mode) or "Partner > [Page]" (cross mode).
          const isPartnerUser = user?.role === 'partner_admin'
            || user?.role === 'partner_viewer';
          if (!isPartnerUser) {
            return (
              <Link
                to="/home"
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="breadcrumb-home"
              >
                Home
              </Link>
            );
          }
          const activeWsId = typeof window !== 'undefined'
            ? window.localStorage.getItem('activeWorkspaceId') : null;
          const partnerCross = typeof window !== 'undefined'
            && window.localStorage.getItem('partnerCrossWorkspaceMode') === 'true';
          const inWorkspaceMode = !!activeWsId && !partnerCross;
          const wsLabel = overviewGlobal ? 'Global (all workspaces)' : (activeWorkspace?.name
            || (activeWsId ? `Workspace ${String(activeWsId).slice(0, 8)}…` : null));
          return (
            <>
              <Link
                to="/partner"
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="breadcrumb-partner"
              >
                Partner
              </Link>
              {(inWorkspaceMode || overviewGlobal) && wsLabel ? (
                <>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <span
                    className="text-muted-foreground"
                    data-testid="breadcrumb-workspace"
                    title={wsLabel}
                  >
                    {wsLabel}
                  </span>
                </>
              ) : null}
            </>
          );
        })()}
        {breadcrumbs.filter(c => c.path !== '/home' && c.path !== '/partner').map((crumb, index, arr) => (
          <React.Fragment key={crumb.path}>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            {index === arr.length - 1 ? (
              <span className="text-foreground font-medium"
                    data-testid={`breadcrumb-current-${crumb.path.replace(/^\//, '').replace(/\//g, '-')}`}>
                {crumb.title}
              </span>
            ) : (
              <Link 
                to={crumb.path} 
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`breadcrumb-link-${crumb.path.replace(/^\//, '').replace(/\//g, '-')}`}
              >
                {crumb.title}
              </Link>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* Workspace switcher + refresh, left of search */}
      <div className="flex items-center gap-3 ml-6 min-w-0" data-testid="topbar-workspace">
        <div className="w-[280px] xl:w-[380px] min-w-0"><WorkspaceSwitcher /></div>
        <Button variant="ghost" size="icon" aria-label="Refresh data" title="Refresh data" data-testid="topbar-refresh"
                onClick={() => window.dispatchEvent(new Event('overview:refresh'))}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Center: Search */}
      <form onSubmit={handleSearch} className="flex-1 max-w-md mx-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            type="search"
            placeholder="Search by Order ID, MRN, or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10 bg-surface"
            data-testid="global-search"
          />
        </div>
      </form>

      {/* Right: Active Workspace, Notifications & User */}
      <div className="flex items-center gap-3">

        {/* Notifications Popover */}
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative" 
              data-testid="notifications-btn"
              onClick={() => {
                setNotifOpen(!notifOpen);
                if (!notifOpen) loadNotifications();
              }}
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h4 className="font-semibold text-sm">Notifications</h4>
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7"
                  onClick={markAllAsRead}
                >
                  Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="h-[300px]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notif) => {
                    const IconComponent = notificationIcons[notif.type] || notificationIcons.default;
                    return (
                      <div
                        key={notif.id}
                        className={cn(
                          "px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors",
                          !notif.read && "bg-blue-50/50"
                        )}
                        onClick={() => handleNotificationClick(notif)}
                      >
                        <div className="flex gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                            notif.type === 'intake_complete' ? 'bg-green-100 text-green-600' :
                            notif.type === 'approval_required' ? 'bg-amber-100 text-amber-600' :
                            'bg-blue-100 text-blue-600'
                          )}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("text-sm", !notif.read && "font-medium")}>
                              {notif.title}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {notif.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatTimeAgo(notif.created_at)}
                            </p>
                          </div>
                          {!notif.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to="/settings"><User className="w-4 h-4 mr-2" /> Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/settings"><Settings className="w-4 h-4 mr-2" /> Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
